import { useEffect, type CSSProperties } from 'react';

import type { VersionNodeSummary } from '../../services/cplex';
import { VersionMetaList } from './VersionMetaList';

// Browse every CPLEX-Modeler version of the current model: when each was
// created, its state, and its full set of constraints / assumptions — so the
// version lineage is explorable, not just a flat dropdown of titles.

interface Props {
  versions: VersionNodeSummary[];
  selectedVersionId: string | null;
  onSelect: (id: string | null) => void;
  onClose: () => void;
}

const OVERLAY: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
};
const CARD: CSSProperties = {
  background: '#fff',
  color: '#1f2937',
  borderRadius: 12,
  width: 'min(720px, 95vw)',
  maxHeight: '86vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
};

const STATE_COLOR: Record<string, string> = {
  ready: '#16a34a',
  failed: '#dc2626',
  building: '#f59e0b',
  solving: '#f59e0b',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function VersionBrowserModal({
  versions,
  selectedVersionId,
  onSelect,
  onClose,
}: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Newest first.
  const sorted = [...versions].sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? ''),
  );

  const pick = (id: string | null) => {
    onSelect(id);
    onClose();
  };

  const rowStyle = (active: boolean): CSSProperties => ({
    width: '100%',
    textAlign: 'left',
    border: active ? '1px solid #1e3a5f' : '1px solid #e2e8f0',
    background: active ? '#f0f6ff' : '#fff',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'pointer',
    font: 'inherit',
  });

  return (
    <div style={OVERLAY} onClick={onClose} role="dialog" aria-modal="true" aria-label="Browse versions">
      <div style={CARD} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <strong style={{ fontSize: 14 }}>
            Versions <span style={{ color: '#64748b', fontWeight: 400 }}>({versions.length})</span>
          </strong>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16, overflow: 'auto' }}>
          <button
            type="button"
            style={rowStyle(selectedVersionId === null)}
            onClick={() => pick(null)}
          >
            <div style={{ fontSize: 13, fontWeight: 700 }}>Base run</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              The original solve (no CPLEX-Modeler edits).
            </div>
          </button>

          {sorted.map((v) => {
            const active = v.id === selectedVersionId;
            const pct = v.summary_deltas?.objective_pct as number | undefined;
            return (
              <button key={v.id} type="button" style={rowStyle(active)} onClick={() => pick(v.id)}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{v.label}</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#fff',
                      background: STATE_COLOR[v.state] ?? '#64748b',
                      borderRadius: 4,
                      padding: '1px 6px',
                    }}
                  >
                    {v.state}
                  </span>
                  {typeof pct === 'number' && (
                    <span style={{ fontSize: 11, color: pct >= 0 ? '#16a34a' : '#dc2626' }}>
                      {pct >= 0 ? '+' : ''}
                      {pct.toFixed(1)}% obj
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                    {fmtDate(v.created_at)}
                  </span>
                </div>
                <VersionMetaList dynamicMetadata={v.dynamic_metadata} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
